import React, { useState } from 'react'
import { I18n, Translate } from 'react-redux-i18n'
import { Checkbox, IconButton, TextField } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import { AuthStatus, LoginError, LoginRequest, useAuth } from 'auth'
import Logo from 'components/ui/Logo'
import { ErrorAlert, LoginButton, LoginTitle, LogoWrapper, RememberMeInput } from './styled'

const errorMessage = (error?: LoginError) => {
  if (!error) return undefined
  switch (error.code) {
    case 'Custom':
      return error.message
    case 'Unauthorized':
      return I18n.t('UI.Auth.Error.Forbidden')
    default:
      return I18n.t('UI.Auth.Error.Unknown')
  }
}

type LoginFormProps = {
  title: React.ReactNode
  onRequest: LoginRequest
}

const LoginForm = ({ title, onRequest }: LoginFormProps) => {
  const { status, error, signIn } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const isLoading = status === AuthStatus.Loading
  const isFilledOut = email !== '' && password !== ''

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    signIn({ email, password, remember }, onRequest)
  }

  return (
    <>
      <LogoWrapper>
        <Logo />
      </LogoWrapper>

      <LoginTitle variant="h5" align="center" color="textPrimary">
        {title}
      </LoginTitle>

      <ErrorAlert $hidden={!error} data-testid="error-alert">
        {errorMessage(error) || ' '}
        {/* Needs some content to keep height correct when hidden */}
      </ErrorAlert>

      <form onSubmit={onSubmit}>
        <TextField
          variant="outlined"
          fullWidth
          label={<Translate value="UI.Auth.Email" />}
          id="login-email"
          name="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          variant="outlined"
          fullWidth
          label={<Translate value="UI.Auth.Password" />}
          id="login-password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />
        <RememberMeInput
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
          }
          label={<Translate value="UI.Auth.Remember" />}
        />

        <LoginButton
          fullWidth
          color="primary"
          variant="contained"
          type="submit"
          disableElevation
          loading={isLoading}
          disabled={!isFilledOut}
        >
          <Translate value="UI.Auth.Login" />
        </LoginButton>
      </form>
    </>
  )
}

export default LoginForm
